import { databases, storage } from "@/lib/appwrite";
import { Query } from "appwrite";
import type { Models } from "appwrite";

const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID!;
const collectionId = import.meta.env.VITE_WORKER_PROFILE_COLLECTION_ID!;
const avatarBucketId = import.meta.env.VITE_BUCKET_ID_AVATAR_COLLABORATOR!;

const PAGE_SIZE = 100;

export type User = { id: string; name: string; email: string };
export type Profile = {
  userId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  secondLastName?: string;
  occupation?: string;
  occupationAbbreviation?: string;
  rolCorp?: string;
  colabCorpCode?: string;
  isActive?: boolean;
  leaveDate?: string;
  idSupervisor?: string;
  area?: string;
  phone1?: string;
  birthday?: string;
  joinDate?: string;
  bloodType?: string;
  keyFunction?: string[];
  archievement?: string[];
  departmentId?: string;
  colabDniCode?: string;
  phone2?: string;
  bank?: string;
  bankAccountNumber?: string;
  bankAccountType?: string;
  address?: string;
};
export type CombinedUser = User & { profile: Profile | null; avatarUrl: string };

class UserCacheManager {
  private static instance: UserCacheManager;
  private cache = new Map<string, {
    data: CombinedUser[];
    timestamp: number;
    avatarMap: Map<string, string>;
  }>();
  private loadingPromise: Promise<CombinedUser[]> | null = null;
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000;

  private constructor() { }

  static getInstance(): UserCacheManager {
    if (!UserCacheManager.instance) {
      UserCacheManager.instance = new UserCacheManager();
    }
    return UserCacheManager.instance;
  }

  async getUsers(forceRefresh = false): Promise<CombinedUser[]> {
    const cacheKey = 'users';
    const cached = this.cache.get(cacheKey);

    if (!forceRefresh && cached && (Date.now() - cached.timestamp < this.CACHE_DURATION)) {
      return cached.data;
    }

    if (this.loadingPromise) {
      try {
        return await this.loadingPromise;
      } catch {
        if (cached) return cached.data;
        throw new Error('Error cargando usuarios');
      }
    }

    this.loadingPromise = this.fetchUsersWithProfiles();

    try {
      const users = await this.loadingPromise;
      this.loadingPromise = null;
      return users;
    } catch (error) {
      this.loadingPromise = null;
      if (cached) return cached.data;
      return [];
    }
  }

  private async fetchUsersWithProfiles(): Promise<CombinedUser[]> {
    const [profilesResult, avatarsResult] = await Promise.allSettled([
      this.fetchProfilesWithTimeout(15000),
      this.fetchAvatarsWithTimeout(10000),
    ]);

    const avatarMap = avatarsResult.status === 'fulfilled'
      ? avatarsResult.value
      : new Map<string, string>();

    if (profilesResult.status === 'rejected') {
      const cached = this.cache.get('users');
      if (cached) return cached.data;
      return [];
    }

    const combinedUsers: CombinedUser[] = profilesResult.value.documents.map((doc: any) => ({
      id: doc.userId,
      name: `${doc.firstName ?? ''} ${doc.lastName ?? ''}`.trim() || 'Sin nombre',
      email: doc.email ?? 'Sin email',
      profile: doc as Profile,
      avatarUrl: avatarMap.get(doc.userId) ?? '/images/perfil.png',
    }));

    this.cache.set('users', {
      data: combinedUsers,
      timestamp: Date.now(),
      avatarMap,
    });

    return combinedUsers;
  }

  // Trae TODOS los perfiles paginando de a 100 (Appwrite limita a 25 por defecto)
  private async fetchAllProfiles(): Promise<{ documents: any[] }> {
    const documents: any[] = [];
    let cursor: string | undefined;

    while (true) {
      const queries = [Query.limit(PAGE_SIZE)];
      if (cursor) queries.push(Query.cursorAfter(cursor));

      const res = await databases.listDocuments(databaseId, collectionId, queries);
      documents.push(...res.documents);

      if (res.documents.length < PAGE_SIZE) break;
      cursor = res.documents[res.documents.length - 1].$id;
    }

    return { documents };
  }

  private async fetchProfilesWithTimeout(timeout: number): Promise<any> {
    return Promise.race([
      this.fetchAllProfiles(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout perfiles')), timeout)
      ),
    ]);
  }

  private async fetchAvatarsWithTimeout(timeout: number): Promise<Map<string, string>> {
    try {
      return await Promise.race([
        this.getAvatarMap(),
        new Promise<Map<string, string>>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout avatares')), timeout)
        ),
      ]);
    } catch {
      return new Map();
    }
  }

  private async getAvatarMap(): Promise<Map<string, string>> {
    try {
      const files: Models.File[] = [];
      let cursor: string | undefined;

      while (true) {
        const queries = [Query.limit(PAGE_SIZE)];
        if (cursor) queries.push(Query.cursorAfter(cursor));

        const res = await storage.listFiles(avatarBucketId, queries);
        files.push(...res.files);

        if (res.files.length < PAGE_SIZE) break;
        cursor = res.files[res.files.length - 1].$id;
      }

      const avatarEntries = files.map((file: Models.File) => {
        try {
          const url = storage.getFilePreview(avatarBucketId, file.$id, 100, 100);
          return [file.$id, url.toString()] as const;
        } catch {
          return null;
        }
      });

      return new Map(
        avatarEntries.filter((e): e is readonly [string, string] => e !== null)
      );
    } catch {
      return new Map();
    }
  }

  getUsersMap(): Map<string, { name: string; email: string; profile: Profile | null }> {
    const cached = this.cache.get('users');
    if (!cached) return new Map();

    const map = new Map<string, { name: string; email: string; profile: Profile | null }>();
    for (const user of cached.data) {
      map.set(user.id, {
        name: user.name,
        email: user.email,
        profile: user.profile,
      });
    }
    return map;
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const userCacheManager = UserCacheManager.getInstance();
export const fetchUsersWithProfiles = () => userCacheManager.getUsers();
export const clearUsersCache = () => userCacheManager.clearCache();